import Highcharts from 'highcharts'

// Shared by the legacy (src/pages/analytics) and new-style (src/pages-new/analytics) Analytics
// pages of the patient / insurance user: the contract of psp-backend GET /therapies/adherence
// and /secondary-therapies/adherence (one per tab) and the Highcharts options built on it, so
// the two UIs can never compute or draw adherence differently.

export type AdherenceGranularity = 'day' | 'week' | 'month'

export interface AdherenceRatio {
  completed: number
  total: number
  // null when nothing was due: "no data", never 0% adherence
  percentage: number | null
}

export interface AdherencePeriod extends AdherenceRatio {
  // first day (YYYY-MM-DD, Europe/Rome) of the day/week/month the entry covers
  periodStart: string
}

export type AdherenceTherapyState = 'ACTIVE' | 'NOT_ACTIVE'

export interface AdherenceTherapy extends AdherenceRatio {
  therapyId: string
  drugName: string
  // only the assumptions due between startDate and endDate (if any) are counted
  state?: AdherenceTherapyState
  startDate?: string
  endDate?: string
}

export interface AdherenceSection extends AdherenceRatio {
  timeline: AdherencePeriod[]
  byTherapy: AdherenceTherapy[]
}

// The adherence of one kind of therapy, plus the range it was actually computed on (after the
// backend's defaults and its cap on "now")
export interface AdherenceResponse extends AdherenceSection {
  dateFrom: string
  dateTo: string
  granularity: AdherenceGranularity
  // options of the "Farmaco" filter: every drug with something due in the range, whatever drug is
  // selected (absent from a backend older than the filter)
  drugNames?: string[]
}

export type AdherenceTherapyType = 'primary' | 'secondary'

export const ADHERENCE_DEFAULT_RANGE_DAYS = 30

// "Stato terapia" filter: empty value = every therapy, whatever its state
export const ADHERENCE_THERAPY_STATE_OPTIONS: {value: AdherenceTherapyState; label: string}[] = [
  {value: 'ACTIVE', label: 'Attive'},
  {value: 'NOT_ACTIVE', label: 'Non attive'},
]

// Query of GET /therapies/adherence and /secondary-therapies/adherence
export interface AdherenceFilters {
  dateFrom?: string
  dateTo?: string
  therapyState?: string
  drugName?: string
}

// Filters of the Analytics page. Which are shown (and their labels) comes from
// analyticsConfigurations.filtersConfig, configured via the PSP configurator's "Analytics" tab:
// keep the ids in sync with analyticsFilterOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.
export const ADHERENCE_FILTER_IDS = {
  DATE: 'date',
  THERAPY_STATE: 'therapyState',
  DRUG_NAME: 'drugName',
} as const

export interface AdherenceFilterConfig {
  id: string
  label?: string
}

const ADHERENCE_FILTER_LABELS: Record<string, string> = {
  [ADHERENCE_FILTER_IDS.DATE]: 'Data',
  [ADHERENCE_FILTER_IDS.THERAPY_STATE]: 'Stato terapia',
  [ADHERENCE_FILTER_IDS.DRUG_NAME]: 'Farmaco',
}

// A programme never saved from the configurator has no filtersConfig: every filter is shown, as
// before the filters were configurable. A saved one shows exactly the filters it lists (none
// at all included).
export function resolveAdherenceFiltersConfig(filtersConfig?: AdherenceFilterConfig[]): AdherenceFilterConfig[] {
  if (!Array.isArray(filtersConfig)) {
    return Object.values(ADHERENCE_FILTER_IDS).map(id => ({id}))
  }
  return filtersConfig.filter(({id}) => Object.prototype.hasOwnProperty.call(ADHERENCE_FILTER_LABELS, id))
}

// Tabs (Terapie / Terapie Accessorie) and titles of the Analytics page, from
// analyticsConfigurations.sections / .labels of the PSP configurator's "Analytics" tab: keep the
// keys in sync with analyticsSectionOptions / analyticsLabelOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.
export interface AdherenceSectionConfig {
  enabled?: boolean
  label?: string
}

export interface AdherenceSectionsConfig {
  therapies?: AdherenceSectionConfig
  secondaryTherapies?: AdherenceSectionConfig
}

export interface AdherenceLabels {
  totalTitle: string
  adherence: string
  completedAssumptions: string
  expectedAssumptions: string
  timelineTitle: string
  byTherapyTitle: string
}

export const ADHERENCE_DEFAULT_LABELS: AdherenceLabels = {
  totalTitle: 'Totale nel periodo',
  adherence: 'Aderenza',
  completedAssumptions: 'Assunzioni effettuate',
  expectedAssumptions: 'Assunzioni previste',
  timelineTitle: 'Andamento dell\'aderenza',
  byTherapyTitle: 'Aderenza per terapia',
}

export interface AdherenceTab {
  type: AdherenceTherapyType
  label: string
}

// A section is shown unless the configuration disables it (a programme never saved from the
// configurator shows both, as before they were configurable); the secondary one also needs the
// patient to have some secondary therapies.
export function resolveAdherenceTabs(sections: AdherenceSectionsConfig | undefined, hasSecondaryTherapies: boolean): AdherenceTab[] {
  const tabs: AdherenceTab[] = []
  if (sections?.therapies?.enabled !== false) {
    tabs.push({type: 'primary', label: sections?.therapies?.label || 'Terapie'})
  }
  if (sections?.secondaryTherapies?.enabled !== false && hasSecondaryTherapies) {
    tabs.push({type: 'secondary', label: sections?.secondaryTherapies?.label || 'Altre terapie'})
  }
  return tabs
}

// The secondary therapies are only worth loading when their section can be shown at all
export function isSecondaryAdherenceEnabled(sections: AdherenceSectionsConfig | undefined): boolean {
  return sections?.secondaryTherapies?.enabled !== false
}

// An empty or missing label falls back to the standard one
export function resolveAdherenceLabels(labels?: Partial<AdherenceLabels>): AdherenceLabels {
  return (Object.keys(ADHERENCE_DEFAULT_LABELS) as (keyof AdherenceLabels)[])
    .reduce((acc, key) => ({...acc, [key]: labels?.[key] || ADHERENCE_DEFAULT_LABELS[key]}), {} as AdherenceLabels)
}

interface AdherenceFilterItem<Range> {
  id: string
  label: string
  type: 'rangePicker' | 'select'
  defaultValue?: Range
  options?: {value: string; label: string}[]
  showSearch?: boolean
}

// Items of the Filters component, shared by the legacy and new-style pages: each passes its own
// pre-filled date range (moment vs dayjs). `drugNames` are the options of the current tab.
export function buildAdherenceFilterItems<Range>(
  filtersConfig: AdherenceFilterConfig[],
  defaultRange: Range,
  drugNames: string[]
): AdherenceFilterItem<Range>[] {
  return filtersConfig.map(({id, label}) => {
    const itemLabel = label || ADHERENCE_FILTER_LABELS[id]
    if (id === ADHERENCE_FILTER_IDS.DATE) {
      return {id, label: itemLabel, type: 'rangePicker', defaultValue: defaultRange}
    }
    if (id === ADHERENCE_FILTER_IDS.THERAPY_STATE) {
      // empty = every therapy; only the doses due while each therapy was prescribed count anyway
      return {id, label: itemLabel, type: 'select', options: ADHERENCE_THERAPY_STATE_OPTIONS}
    }
    return {id, label: itemLabel, type: 'select', showSearch: true, options: drugNames.map(name => ({value: name, label: name}))}
  })
}

// Filters component results -> adherence query. A filter left empty (or hidden by the
// configuration) is simply not sent: without dates the backend falls back to the last 30 days.
export function toAdherenceFilters(results: {property: string; value?: any}[]): AdherenceFilters {
  const valueOf = (id: string) => results.find(item => item.property === id)?.value
  const date = valueOf(ADHERENCE_FILTER_IDS.DATE)
  return {
    dateFrom: date?.[0] ?? undefined,
    dateTo: date?.[1] ?? undefined,
    therapyState: valueOf(ADHERENCE_FILTER_IDS.THERAPY_STATE) || undefined,
    drugName: valueOf(ADHERENCE_FILTER_IDS.DRUG_NAME) || undefined,
  }
}

export function formatAdherencePercentage(percentage: number | null | undefined): string {
  return percentage === null || percentage === undefined
    ? '—'
    : `${percentage.toLocaleString('it-IT', {maximumFractionDigits: 1})}%`
}

// periodStart is a calendar day, not an instant: parsed as local date parts so no timezone shift
// can move it to the previous day.
function parsePeriodStart(periodStart: string): Date {
  const [year, month, day] = periodStart.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function formatAdherencePeriod(periodStart: string, granularity: AdherenceGranularity): string {
  const date = parsePeriodStart(periodStart)
  if (granularity === 'month') {
    return date.toLocaleDateString('it-IT', {month: 'short', year: 'numeric'})
  }
  const dayMonth = date.toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit'})
  return granularity === 'week' ? `Sett. ${dayMonth}` : dayMonth
}

function ratioTooltip(label: string, point: AdherenceRatio | undefined, extra = ''): string {
  if (!point || point.total === 0) {
    return `<b>${label}</b><br/>Nessuna assunzione prevista${extra}`
  }
  return `<b>${label}</b><br/>Aderenza: ${formatAdherencePercentage(point.percentage)}<br/>Effettuate ${point.completed} su ${point.total}${extra}`
}

const THERAPY_STATE_LABELS: Record<AdherenceTherapyState, string> = {
  ACTIVE: 'Attiva',
  NOT_ACTIVE: 'Non attiva',
}

function formatTherapyDate(date: string): string {
  const parsed = new Date(date)
  return isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'})
}

// "Terapia dal 01/09/2026 al 30/09/2026 · Non attiva": the period the adherence of the bar is
// computed on, since only the doses due while the therapy was prescribed are counted.
export function formatTherapyPeriod(therapy: AdherenceTherapy): string {
  const period = [
    therapy.startDate ? `dal ${formatTherapyDate(therapy.startDate)}` : '',
    therapy.endDate ? `al ${formatTherapyDate(therapy.endDate)}` : '',
  ].filter(Boolean).join(' ')
  const state = therapy.state ? THERAPY_STATE_LABELS[therapy.state] : undefined
  return [period ? `Terapia ${period}` : '', state ?? ''].filter(Boolean).join(' · ')
}

const percentageYAxis: Highcharts.YAxisOptions = {
  min: 0,
  max: 100,
  title: {text: undefined},
  labels: {format: '{value}%'},
}

// Adherence over time: a line, one point per day/week/month. Periods with nothing due are null
// gaps in the line rather than a drop to 0%.
export function buildAdherenceTimelineOptions(
  section: AdherenceSection,
  granularity: AdherenceGranularity,
  color: string
): Highcharts.Options {
  const labels = section.timeline.map(period => formatAdherencePeriod(period.periodStart, granularity))

  return {
    chart: {type: 'line', height: 260},
    title: {text: undefined},
    xAxis: {categories: labels, labels: {style: {fontSize: '10px'}}},
    yAxis: percentageYAxis,
    legend: {enabled: false},
    credits: {enabled: false},
    tooltip: {
      formatter() {
        const index = typeof this.point?.index === 'number' ? this.point.index : -1
        return ratioTooltip(String(this.x), section.timeline[index])
      },
    },
    plotOptions: {
      line: {connectNulls: false, lineWidth: 3, marker: {enabled: true, radius: 5, symbol: 'circle'}},
    },
    series: [{
      type: 'line',
      name: 'Aderenza',
      color,
      data: section.timeline.map(period => period.percentage),
    }],
  }
}

// Adherence split by therapy over the whole range: one bar per drug.
export function buildAdherenceByTherapyOptions(section: AdherenceSection, color: string): Highcharts.Options {
  const labels = section.byTherapy.map(therapy => therapy.drugName || 'Terapia senza nome')

  return {
    chart: {type: 'column', height: 260},
    title: {text: undefined},
    xAxis: {categories: labels, labels: {style: {fontSize: '11px'}}},
    yAxis: percentageYAxis,
    legend: {enabled: false},
    credits: {enabled: false},
    tooltip: {
      formatter() {
        const index = typeof this.point?.index === 'number' ? this.point.index : -1
        const therapy = section.byTherapy[index]
        const period = therapy ? formatTherapyPeriod(therapy) : ''
        return ratioTooltip(String(this.x), therapy, period ? `<br/>${period}` : '')
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        maxPointWidth: 64,
        dataLabels: {enabled: true, format: '{y}%'},
      },
    },
    series: [{
      type: 'column',
      name: 'Aderenza',
      color,
      data: section.byTherapy.map(therapy => therapy.percentage),
    }],
  }
}
